
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { DollarSign, Edit, Plus, Trash2 } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

type SubscriptionPlan = Database['public']['Tables']['subscription_plans']['Row'];

export default function PricingManagement() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    api_calls_limit: 0,
    max_file_size: 0,
    monthly_space_limit: 0,
    price_cents: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_cents', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: "Error",
        description: "Failed to fetch subscription plans",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      api_calls_limit: 0,
      max_file_size: 0,
      monthly_space_limit: 0,
      price_cents: 0,
      is_active: true,
    });
    setEditingPlan(null);
  };

  const openEditDialog = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      api_calls_limit: plan.api_calls_limit,
      max_file_size: plan.max_file_size,
      monthly_space_limit: plan.monthly_space_limit,
      price_cents: plan.price_cents,
      is_active: plan.is_active,
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingPlan) {
        // Update existing plan
        const { error } = await supabase
          .from('subscription_plans')
          .update(formData)
          .eq('id', editingPlan.id);

        if (error) throw error;

        setPlans(plans.map(plan => 
          plan.id === editingPlan.id ? { ...plan, ...formData } : plan
        ));

        toast({
          title: "Success",
          description: "Subscription plan updated successfully",
        });
      } else {
        // Create new plan
        const { data, error } = await supabase
          .from('subscription_plans')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;

        setPlans([...plans, data]);

        toast({
          title: "Success",
          description: "Subscription plan created successfully",
        });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: "Error",
        description: "Failed to save subscription plan",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;

    try {
      const { error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', planId);

      if (error) throw error;

      setPlans(plans.filter(plan => plan.id !== planId));

      toast({
        title: "Success",
        description: "Subscription plan deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast({
        title: "Error",
        description: "Failed to delete subscription plan",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
              <span>Pricing Management</span>
            </CardTitle>
            <CardDescription>
              Manage subscription plans and pricing
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Plan</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
                </DialogTitle>
                <DialogDescription>
                  {editingPlan ? 'Update the subscription plan details' : 'Create a new subscription plan for users'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Plan Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Pro, Premium"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Price (USD)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formatPrice(formData.price_cents)}
                    onChange={(e) => setFormData({ ...formData, price_cents: Math.round(parseFloat(e.target.value) * 100) })}
                    placeholder="29.99"
                  />
                </div>
                <div>
                  <Label htmlFor="api_calls">API Calls Limit</Label>
                  <Input
                    id="api_calls"
                    type="number"
                    value={formData.api_calls_limit}
                    onChange={(e) => setFormData({ ...formData, api_calls_limit: parseInt(e.target.value) })}
                    placeholder="100"
                  />
                </div>
                <div>
                  <Label htmlFor="file_size">Max File Size (GB)</Label>
                  <Input
                    id="file_size"
                    type="number"
                    step="0.1"
                    value={(formData.max_file_size / (1024 * 1024 * 1024)).toFixed(1)}
                    onChange={(e) => setFormData({ ...formData, max_file_size: Math.round(parseFloat(e.target.value) * 1024 * 1024 * 1024) })}
                    placeholder="5.0"
                  />
                </div>
                <div>
                  <Label htmlFor="storage">Monthly Storage (GB)</Label>
                  <Input
                    id="storage"
                    type="number"
                    value={(formData.monthly_space_limit / (1024 * 1024 * 1024)).toFixed(0)}
                    onChange={(e) => setFormData({ ...formData, monthly_space_limit: parseInt(e.target.value) * 1024 * 1024 * 1024 })}
                    placeholder="100"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
                <div className="flex space-x-2 pt-4">
                  <Button onClick={handleSave} className="flex-1">
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {plans.map((plan) => (
            <div key={plan.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-lg">{plan.name}</h4>
                  <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant="outline">
                    ${formatPrice(plan.price_cents)}/month
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">API Calls:</span> {plan.api_calls_limit}
                  </div>
                  <div>
                    <span className="font-medium">Max File Size:</span> {formatFileSize(plan.max_file_size)}
                  </div>
                  <div>
                    <span className="font-medium">Monthly Storage:</span> {formatFileSize(plan.monthly_space_limit)}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(plan)}
                  className="flex items-center space-x-1"
                >
                  <Edit className="h-3 w-3" />
                  <span>Edit</span>
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(plan.id)}
                  className="flex items-center space-x-1"
                >
                  <Trash2 className="h-3 w-3" />
                  <span>Delete</span>
                </Button>
              </div>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No subscription plans found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
